<template>
    <div id="favoritesTable">
        <div v-if="loading" class="text-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>

        <div v-else-if="assets.length > 0">
            <div class="d-flex">
                <h3>
                    Favorite Assets
                    <a v-if="!isFullPage" href="/advertiser/favorites" title="View all favorites">View all</a>
                </h3>
                <div v-if="perPageOptions.length > 0" class="ml-auto select-blue mb-2">
                    <select
                        v-model="selectedPerPage"
                        @change="changePerPage"
                        class="form-control"
                    >
                        <option v-for="option in perPageOptions" :key="option" :value="option">
                            {{ option }}
                        </option>
                    </select>
                </div>
            </div>

            <div v-if="navPosition === 'top'" class="row">
                <div class="col pagination-sm">
                    <nav>
                        <ul class="pagination pagination-sm">
                            <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                <a class="page-link" @click.prevent="goToPage(pagination.current_page - 1)" href="#">Previous</a>
                            </li>
                            <li
                                v-for="page in visiblePages"
                                :key="page"
                                class="page-item"
                                :class="{ active: page === pagination.current_page }"
                            >
                                <a class="page-link" @click.prevent="goToPage(page)" href="#">{{ page }}</a>
                            </li>
                            <li class="page-item"
                                :class="{ disabled: pagination.current_page === pagination.last_page }">
                                <a class="page-link" @click.prevent="goToPage(pagination.current_page + 1)" href="#">Next</a>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            <div class="row">
                <div
                    v-for="asset in assets"
                    :key="asset.id"
                    class="col-3 d-flex align-items-stretch"
                    :data-test="'favorites-asset-' + asset.id"
                >
                    <asset-card
                        :asset="transformAssetForCard(asset)"
                        :enable-toggle-favorite="enableToggleFavorite"
                        @favorite-toggled="handleFavoriteToggled"
                    />
                </div>
            </div>

            <div v-if="navPosition !== 'top'" class="row">
                <div class="col">
                    <nav>
                        <ul class="pagination pagination-sm">
                            <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                                <a class="page-link" @click.prevent="goToPage(pagination.current_page - 1)" href="#">Previous</a>
                            </li>
                            <li
                                v-for="page in visiblePages"
                                :key="page"
                                class="page-item"
                                :class="{ active: page === pagination.current_page }"
                            >
                                <a class="page-link" @click.prevent="goToPage(page)" href="#">{{ page }}</a>
                            </li>
                            <li class="page-item"
                                :class="{ disabled: pagination.current_page === pagination.last_page }">
                                <a class="page-link" @click.prevent="goToPage(pagination.current_page + 1)" href="#">Next</a>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </div>

        <div v-else-if="!loading" class="no-data">
            No favorites
        </div>
    </div>
</template>

<script>
import AssetCard from '../shared/cards/AssetCard.vue';

export default {
    name: 'FavoritesTable',

    components: {
        AssetCard,
    },

    props: {
        options: {
            type: Object,
            default: () => ({}),
        },
        navPosition: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            assets: [],
            loading: true,
            selectedPerPage: 12,
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 12,
                total: 0,
                from: 0,
                to: 0,
                prev_page_url: null,
                next_page_url: null,
            },
        };
    },

    computed: {
        perPage() {
            return parseInt(this.selectedPerPage) || 12;
        },

        perPageOptions() {
            return this.options.per_page_options || [12, 24, 48];
        },

        enableToggleFavorite() {
            return this.options.enable_toggle_favorite || false;
        },

        isFullPage() {
            return window.location.pathname === '/advertiser/favorites';
        },

        visiblePages() {
            const current = this.pagination.current_page;
            const last = this.pagination.last_page;
            const pages = [];

            // Show up to 5 page numbers around current page
            const start = Math.max(1, current - 2);
            const end = Math.min(last, current + 2);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            return pages;
        },
    },

    mounted() {
        // Set initial per_page from options
        this.selectedPerPage = this.options.per_page || 12;
        this.loadFavorites();
    },

    methods: {
        async loadFavorites(page = 1) {
            try {
                this.loading = true;

                const response = await axios.get('/api/favorites/assets', {
                    params: {
                        per_page: this.perPage,
                        page: page,
                    },
                });

                this.assets = response.data.data;
                this.pagination = {
                    current_page: response.data.current_page,
                    last_page: response.data.last_page,
                    per_page: response.data.per_page,
                    total: response.data.total,
                    from: response.data.from,
                    to: response.data.to,
                    prev_page_url: response.data.prev_page_url,
                    next_page_url: response.data.next_page_url,
                };
            } catch (error) {
                console.error('Failed to load favorites:', error);
                this.assets = [];
            } finally {
                this.loading = false;
            }
        },

        changePerPage() {
            this.loadFavorites(1); // Reset to first page when changing per page
        },

        goToPage(page) {
            if (page >= 1 && page <= this.pagination.last_page && page !== this.pagination.current_page) {
                this.loadFavorites(page);
            }
        },

        handleFavoriteToggled(assetId) {
            // Remove the asset from the list if it was unfavorited
            this.assets = this.assets.filter(asset => asset.id !== assetId);

            // If we're on the last page and it becomes empty, go to previous page
            if (this.assets.length === 0 && this.pagination.current_page > 1) {
                this.goToPage(this.pagination.current_page - 1);
            } else if (this.assets.length === 0) {
                // If it's the first page and empty, reload to update pagination
                this.loadFavorites(1);
            }
        },

        transformAssetForCard(asset) {
            // Transform the API response format to match what AssetCard expects
            return {
                id: asset.id,
                name: asset.name,
                image: asset.asset_image_url || asset.asset_image,
                asset_image: asset.asset_image,
                asset_image_url: asset.asset_image_url,
                is_favorite: asset.is_favorite,
                online_price: asset.online_price,
                cheapest_product_price: asset.cheapest_product_price,
                price: asset.cheapest_product_price || asset.online_price,
                team_name: asset.team?.name,
                team_logo: asset.team?.photo_url,
                venue_name: asset.team?.venue_name,
                league_name: asset.team?.league?.name,
                market: asset.team?.market?.name,
                // For compatibility with existing AssetCard component
                team: asset.team,
            };
        },
    },
};
</script>

<style scoped>
.no-data {
    text-align: center;
    padding: 2rem;
    color: #6c757d;
}

.select-blue select {
    border-color: #007bff;
}

.pagination-sm .page-link {
    cursor: pointer;
}

.pagination-sm .page-item.disabled .page-link {
    cursor: not-allowed;
}
</style>
